// 네이티브(Capacitor) 전용 브리지.
//  1) 앱 시작 스플래시(네이티브)를 웹 로드 후 숨긴다(capacitor.config launchAutoHide=false 이므로 필수).
//  2) 소셜 로그인 딥링크 복귀 처리: 서버 OAuth 콜백이 openchuksan://auth/social-callback?linkToken=...&provider=...
//     로 302 하면 OS 가 앱을 열고 App.appUrlOpen 이벤트가 발생한다. 토큰을 파싱해 소셜 콜백 화면으로 넘긴다.
// 웹(PWA/브라우저)에서는 isNativeApp=false 라 아무 것도 하지 않는다(기존 동작 유지).

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { App as CapacitorApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { PushNotifications } from "@capacitor/push-notifications";
import { SplashScreen } from "@capacitor/splash-screen";
import { useAuthStore } from "@/stores/authStore.ts";
import { syncNativePushOnLaunch } from "@/features/push/nativePush.ts";
import {
  NATIVE_PAYMENT_RETURN_PARAM,
  claimPendingDirectPaymentRecovery,
  markPendingDirectPaymentRecoveryShown,
} from "@/features/payments/directPayment.ts";
import { isNativeApp, SOCIAL_CALLBACK_PATH } from "./platform.ts";

// 네이티브 전용 부수효과(스플래시 숨김 + 딥링크 복귀)를 담는 렌더 없는 컴포넌트. 웹에선 아무 것도 하지 않는다.
export default function NativeBridge() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNativeApp) {
      return;
    }
    // 웹 컨텐츠가 마운트됐으니 네이티브 스플래시를 내린다.
    void SplashScreen.hide();

    // 소셜 로그인과 결제 완료 딥링크를 같은 진입점에서 처리한다.
    const handleIncomingUrl = (url: string | undefined) => {
      if (!url) return;
      if (url.startsWith("openchuksan://payment-complete")) {
        const query = url.split("?")[1] ?? "";
        const params = new URLSearchParams(query);
        const orderId = params.get("orderId");
        if (!orderId || !/^[0-9a-f-]{36}$/iu.test(orderId)) return;
        markPendingDirectPaymentRecoveryShown(orderId);
        params.delete("orderId");
        const suffix = params.toString();
        navigate(`/me/orders/${orderId}${suffix ? `?${suffix}` : ""}`, { replace: true });
        return;
      }
      if (!url.includes(SOCIAL_CALLBACK_PATH)) return;
      // 스킴/호스트 파싱 편차를 피하려고 쿼리스트링만 뽑아 파싱한다.
      const query = url.split("?")[1] ?? "";
      const params = new URLSearchParams(query);
      const linkToken = params.get("linkToken");
      const provider = params.get("provider");
      // OAuth 를 열었던 인앱 브라우저를 닫는다(실패해도 무시).
      void Browser.close().catch(() => undefined);
      if (!linkToken || !provider) {
        navigate("/login", { replace: true });
        return;
      }
      // 소셜 콜백 화면으로 토큰을 state 로 전달(웹은 fragment, 네이티브는 state 경로).
      navigate(SOCIAL_CALLBACK_PATH, { replace: true, state: { linkToken, provider } });
    };

    // 결제 페이지가 WebView의 앱 문서를 대체한 경우 Android가 앱 루트를 먼저 다시 연다.
    // 루트 쿼리에 실어 온 원래 딥링크를 즉시 소비하고 지워 새로고침 때 반복 처리되지 않게 한다.
    const nativePaymentReturn = new URLSearchParams(window.location.search).get(
      NATIVE_PAYMENT_RETURN_PARAM,
    );
    if (nativePaymentReturn) {
      window.history.replaceState({}, "", "/");
      handleIncomingUrl(nativePaymentReturn);
    }

    const handle = CapacitorApp.addListener("appUrlOpen", ({ url }) => {
      handleIncomingUrl(url);
    });

    // 앱이 꺼진 상태에서 딥링크로 시작된 경우 appUrlOpen 리스너 등록 전 URL도 회수한다.
    void CapacitorApp.getLaunchUrl().then((launch) => {
      // 앱 루트 쿼리로 이미 소비한 결제 딥링크는 다시 라우팅하지 않는다.
      if (launch?.url !== nativePaymentReturn) handleIncomingUrl(launch?.url);
    });

    // 외부 결제 앱에서 단순 복귀했지만 완료 리다이렉트가 늦는 경우 주문 상세가 서버 재조회를 수행한다.
    const stateHandle = CapacitorApp.addListener("appStateChange", ({ isActive }) => {
      if (!isActive) return;
      const orderId = claimPendingDirectPaymentRecovery();
      if (orderId) navigate(`/me/orders/${orderId}`, { replace: true });
    });

    // 푸시 알림 탭 복귀: 서버가 data.url 에 넣어준 앱 내 경로(/me/orders/..)로 이동한다.
    //  - 외부 URL 주입을 막기 위해 "/" 로 시작하는 내부 경로만 허용한다.
    const pushHandle = PushNotifications.addListener("pushNotificationActionPerformed", (ev) => {
      const url = (ev.notification.data as Record<string, unknown> | undefined)?.url;
      if (typeof url === "string" && url.startsWith("/")) {
        navigate(url);
      }
    });

    // 앱 시작 시 FCM 토큰 동기화(알림 켠 기기만). 토큰 회전을 서버에 반영한다.
    void syncNativePushOnLaunch(!!useAuthStore.getState().session);

    return () => {
      void handle.then((h) => h.remove());
      void stateHandle.then((h) => h.remove());
      void pushHandle.then((h) => h.remove()).catch(() => undefined);
    };
  }, [navigate]);

  return null;
}
